import * as Button from '../../../../components/button/button';
import { Icons } from '../../../../components/icons/icons';
import * as Page from '../../../../components/content/page-header';
import { Link } from 'react-router-dom';
import { useMatch, useParams, useNavigate, Routes, Route } from 'react-router';
import { useQuery } from '../../../../hooks/use-query';
import { LayerField, MapLayer } from '../../types/map-layer';
import { FieldValues, useForm } from 'react-hook-form';
import { Label, TextInputLarge, TextInputSmall } from '../../../../components/forms/input';
import { Checkbox2 } from '../../../../components/forms/checkbox';
import { usePostMutation, usePutMutation } from '../../../../hooks/use-mutations';
import { Pageable, usePagination } from '../../../../hooks/use-pagination';
import { ValueFormatter } from '../../../settings/types/formatters';
import React, { useEffect, useState } from 'react';
import { Select2 } from '../../../../components/forms/select';
import { useIsMutating } from 'react-query';
import { Language } from '../../../internationalization/types/language';
import { GearIconColumn } from '../../../../components/table/columns';
import { KeyValue } from '../../../../types/key-value';
import { Table } from '../../../../components/table/table';
import { Translation } from '../../../internationalization/types/translation';
import { LightBlack } from '../../../../components/button/button';
import { alert } from '../../../../components/toast/toast';
import { useDefaultLanguage } from '../../../../hooks/use-default-language';

export const FieldsEditor = (): JSX.Element => {
  const params = useParams();
  const id = params.id as string;
  const fieldId = params.fieldId as string;
  const navigate = useNavigate();
  const isMutating = useIsMutating();
  const mapLayer = useQuery<MapLayer>({ path: `/map-layers/${id}`, options: { enabled: fieldId !== 'new' } });

  return <div>
    <div className="row">
      <div className="col-9 bg-light left-col">
        <Page.Header>
          <div className="breadcrumb">
            <span className="breadcrumb-text">
               <span>Map Layers /</span><span> {params.id === 'new' ? 'New' : mapLayer.data?.name as string} /</span><span className="text-dark-blue"> {params.id === 'new' ? 'Add' : 'Edit'}</span>
            </span>
          </div>
          <Page.Title>
            <Link to={`/map-layers/${id}/fields`} className="page-title-link">
              <Icons.LeftArrow className="page-header-back" />
              {fieldId === 'new' ? 'Add Field' : 'Edit Field'}
            </Link>
          </Page.Title>
        </Page.Header>
        <Tabs />
      </div>
      <div className="col-3 d-flex flex-column">
        <div className="px-5">
          <div className="mb-3">
            <Button.Primary form="current-form" type="submit" disabled={isMutating}>
              {fieldId === 'new' ? <><Icons.Add />Add</>: 'Update'}
            </Button.Primary>
          </div>
          <div className="mb-3">
            <LightBlack form="translations-form" type="submit" disabled={isMutating || fieldId === 'new' || !location.pathname.endsWith('/translations')}>
              Translate
            </LightBlack>
          </div>
          <div className="mb-3">
            <Button.Tertiary type="submit" onClick={(): void => navigate(`/map-layers/${id}/fields`)} disabled={isMutating}>
              Cancel
            </Button.Tertiary>
          </div>
        </div>
      </div>
    </div>
  </div>;
};

const Tabs = (): JSX.Element => {
  const params = useParams();
  const id = params.id as string;
  const fieldId = params.fieldId as string;
  const path = `/map-layers/${id}`;

  const tabItems = [
    { path: `/fields/${fieldId}/editor`, text: 'Info' },
    { path: `/fields/${fieldId}/translations`, text: 'Translation' },
  ];

  return <>
    <div className="tabs">
      {tabItems.map((tab, index: number) => {
        const pathname = `${path}${tab.path}`;
        const match = useMatch(pathname);
        return <span key={tab.path} className="d-flex align-items-center">
          {index === tabItems.length - 1 && fieldId === 'new'
            ? <span className="tab-item disabled">{tab.text}</span>
            : <Link to={pathname} className={`tab-item ${match ? 'tab-item-selected' : ''}`}>{tab.text}</Link>
          }
          {index !== tabItems.length - 1 ? <span className="tab-separator" /> : null}
        </span>;
      })}
    </div>
    <div className="separator" />
    <Routes>
      <Route path="/editor" element={<Editor />} />
      <Route path="/translations" element={<FieldTranslation />} />
    </Routes>
  </>;
};

const Editor = (): JSX.Element => {
  const { register, handleSubmit, getValues, setValue } = useForm();
  const navigate = useNavigate();
  const params = useParams();
  const layerId = params.id as string;
  const fieldId = params.fieldId as string;
  const formatters = usePagination<Pageable<ValueFormatter>>('/formatter', { size: 9999 });
  const field = useQuery<LayerField>({ path: `/map-layers/fields/${fieldId}`, options: { enabled: fieldId !== 'new' } });
  const postMutation = usePostMutation<LayerField>({ path: '/map-layers/fields', cacheKey: `/map-layers/${layerId}/fields` });
  const putMutation = usePutMutation<LayerField>({ path: `/map-layers/fields/${fieldId}`, cacheKey: `/map-layers/${layerId}/fields` });

  useEffect(() => {
    if (field.data?.id) {
      updateField(field.data);
    }
  }, [field]);

  useEffect(() => {
    if (postMutation.data?.id) {
      navigate(`/map-layers/${layerId}/fields/${postMutation?.data?.id}/editor`);
      alert.success('Field created successfully');
    }
  }, [postMutation.isSuccess]);

  useEffect(() => {
    if (putMutation.isSuccess) {
      alert.success('Field updated successfully');
    }
  }, [putMutation.isSuccess]);

  const onSubmit = (data: FieldValues): void => {
    fieldId !== 'new' ? putMutation.mutate({ ...data, layerId }) : postMutation.mutate({ ...data, layerId });
  };

  const updateField = (item: LayerField): void => {
    for (const key in item) {
      if (key === 'id') {
        continue;
      }
      setValue(key, item[key as keyof LayerField]);
    }
  };

  return <form id="current-form" onSubmit={handleSubmit(onSubmit)}>
    <div className="mb-4">
      <Label name="key">Field Name</Label>
      <TextInputLarge {...register('fieldName')} placeholder="Name" />
    </div>
    <div className="mb-4">
      <Label name="key">Field Alias</Label>
      <TextInputLarge {...register('fieldAlias')} placeholder="Field Alias" />
    </div>
    <div className="mb-4">
      <Checkbox2 {...register('displayWithAttributes')} label="Display With Attributes" defaultChecked={getValues().displayWithAttributes} />
    </div>
    <div className="mb-4">
      <Checkbox2 {...register('displayWithMapTips')} label="Display With Map Tips" defaultChecked={getValues().displayWithMapTips} />
    </div>
    <div className="mb-4">
      <Label>Value Formatter Id</Label>
      <Select2 {...register('formatterId')}>
        <option value="">Select an option</option>
        {formatters.data?.content.map((formatter: ValueFormatter) => (
          <option key={formatter.id} value={formatter.id}>{formatter.type}</option>
        ))}
      </Select2>
    </div>
    <div className="mb-4">
      <Checkbox2 {...register('filterAvailable')} label="Filter Available" defaultChecked={getValues().filterAvailable} />
    </div>
    <div className="mb-4">
      <Label>Field Type</Label>
      <Select2 {...register('fieldType')}>
        <option value="">Select an option</option>
        {fieldTypes.map(fieldType => <option key={fieldType.value} value={fieldType.value}>{fieldType.label}</option>)}
      </Select2>
    </div>
    <div className="mb-4">
      <Label>Timeline Axis</Label>
      <Select2 {...register('timeLineAxis')}>
        <option value="">Select an option</option>
        {axis.map(axis => <option key={axis.value} value={axis.value}>{axis.label}</option>)}
      </Select2>
    </div>
  </form>;
};

const FieldTranslation = (): JSX.Element => {
  const params = useParams();
  const fieldId = params.fieldId as string;
  const { register, handleSubmit: handleTranslationSubmit, setValue } = useForm();
  const { handleSubmit } = useForm();
  const languages = useQuery<Language[]>({ path: '/languages/all' });
  const defaultLang = useDefaultLanguage();
  const field = useQuery<LayerField>({ path: `/map-layers/fields/${fieldId}` });
  const postMutationBatch = usePostMutation<Translation[]>({ path: '/translation' });
  const putMutation = usePutMutation<LayerField>({ path: `/map-layers/fields/${fieldId}` });
  const [translations, setTranslations] = useState<Translation[]>();

  useEffect(() => {
    if (field.isSuccess && languages.isSuccess) {
      setExistingTranslations();
    }
  }, [field.isSuccess, languages.isSuccess]);

  const columns = [
    ...GearIconColumn(),
    { accessor: 'name', Header: 'Language', sort: true },
    {
      accessor: 'value',
      Header: 'Name',
      Cell: (item: any): JSX.Element => (
        <TextInputSmall
          {...register(`values[${item.row.original.code as string}`, { required: item.row.original.code === defaultLang })}
        />
      )
    }
  ];

  const createValueTranslations = (data: KeyValue): KeyValue[] => {
    return Object.entries(data)
      .filter(([key]) => key !== defaultLang)
      .map(([key]): Translation => {
        return {
          'key': key,
          'from': defaultLang,
          'to': key,
          'phrase': data[defaultLang],
        };
      });
  };

  const onTranslate = async (data: FieldValues): Promise<void> => {
    const valuesToTranslate = createValueTranslations(data.values as KeyValue);
    try {
      const values = await postMutationBatch.mutateAsync(valuesToTranslate);
      values?.forEach?.((item: Translation) => {
        setValue(`values[${item.key}]`, item.translation);
      });
      setTranslations(values);
    } catch(exception) {
      console.error('Exception', exception);
    }
  };

  const saveTranslations = (): void => {
    const data = translations?.map(item => {
      return {
        value: item.translation,
        lang: item.key
      };
    });
    putMutation.mutate({ ...field.data, translations: data });
  };

  const setExistingTranslations = (): void => {
    field.data?.translations.forEach(item => {
      const language = languages?.data?.find((language: Language) => language.code === item.lang);
      if (language) {
        const code = language.code;
        setValue(`values[${code}]`, item.value);
      }
    });
    setValue(`values[${defaultLang}`, field.data?.fieldName);
  };

  return <>
    <form id="current-form" onSubmit={handleSubmit(saveTranslations)} />
    <form id="translations-form" onSubmit={handleTranslationSubmit(onTranslate)}>
      <Table isLoading={languages.isLoading} columns={columns} rows={languages.data || []} />
    </form>
  </>;
};

const fieldTypes = [
  { value: 'Date', label: 'Date' },
  { value: 'Numeric', label: 'Numeric' },
  { value: 'Text', label: 'Text' },
  { value: 'Other', label: 'Other' }
];

const axis = [
  { value: 'X', label: 'X' },
  { value: 'Y', label: 'Y' }
];
